import { Routes, Route, Navigate, useParams } from "react-router-dom"
import { lazy, Suspense, useEffect, useState } from "react"
import { useAuth as useClerkAuth } from "@clerk/react"
import { useAuth } from "@/hooks/useAuth"
import { useDoctorTier } from "@/hooks/useDoctorTier"
import { isDoctorHost, isMarketingHost } from "@/lib/host"
import { api } from "@/lib/api-client"
import Layout from "@/components/Layout"
import ErrorBoundary from "@/components/ErrorBoundary"

// Eager: first-paint surfaces (marketing + sign-in)
import LandingPage from "@/pages/Landing"
import LoginPage from "@/pages/Login"
import AuthCallbackPage from "@/pages/AuthCallback"

// Lazy: every other route ships only when navigated to.
const DashboardPage           = lazy(() => import("@/pages/Dashboard"))
const ApiKeysPage             = lazy(() => import("@/pages/ApiKeys"))
const UsagePage               = lazy(() => import("@/pages/Usage"))
const DocsPage                = lazy(() => import("@/pages/Docs"))
const TenantsPage             = lazy(() => import("@/pages/admin/Tenants"))
const ClinicalReferencePage   = lazy(() => import("@/pages/admin/ClinicalReference"))
const ResearchPage            = lazy(() => import("@/pages/admin/Research"))
const ReviewQueuePage         = lazy(() => import("@/pages/admin/ReviewQueue"))
const AdminSecurityPage       = lazy(() => import("@/pages/admin/Security"))
const AdminObservabilityPage  = lazy(() => import("@/pages/admin/Observability"))
const AdminTemplatesPage      = lazy(() => import("@/pages/admin/Templates"))
const AdminAppSettingsPage    = lazy(() => import("@/pages/admin/AppSettings"))
const AdminValidatorsPage     = lazy(() => import("@/pages/admin/Validators"))
const TemplatesPage           = lazy(() => import("@/pages/Templates"))
const TemplateBuilder         = lazy(() => import("@/pages/TemplateBuilder"))
const DoctorTemplatesPage     = lazy(() => import("@/pages/doctor/Templates"))
const ProductConsolePage      = lazy(() => import("@/pages/ProductConsole"))
const WorklistPage            = lazy(() => import("@/pages/Worklist"))
const SummariesPage           = lazy(() => import("@/pages/Summaries"))
const PatientsPage            = lazy(() => import("@/pages/Patients"))
const WebhooksPage            = lazy(() => import("@/pages/Webhooks"))
const SettingsPage            = lazy(() => import("@/pages/Settings"))
const OnboardingPage          = lazy(() => import("@/pages/Onboarding"))
const TenantsUserPage         = lazy(() => import("@/pages/Tenants"))
const TenantNewPage           = lazy(() => import("@/pages/TenantNew"))
const PaywallPage             = lazy(() => import("@/pages/Paywall"))
const BillingPage             = lazy(() => import("@/pages/Billing"))
const AdminBillingPage        = lazy(() => import("@/pages/admin/Billing"))
const AdminRoadmapPage        = lazy(() => import("@/pages/admin/Roadmap"))
const AdminDesktopDecisionsPage = lazy(() => import("@/pages/admin/DesktopDecisions"))
const AdminExplorePage        = lazy(() => import("@/pages/admin/Explore"))
const D2cPatientDashboard     = lazy(() => import("@/pages/d2c/PatientDashboard"))
const D2cPatientSummary       = lazy(() => import("@/pages/d2c/PatientSummary"))
// /doctor/* routes superseded 2026-05-09 — redirect to /app/cloud (unified
// doctor surface). Old pages parked at apps/web/src/pages/_deprecated/d2c/.
const D2cDoctorUpgrade        = lazy(() => import("@/pages/d2c/DoctorUpgrade"))
const AdminDoctorApprovals    = lazy(() => import("@/pages/admin/DoctorApprovals"))
const D2cPublicShare          = lazy(() => import("@/pages/d2c/PublicShare"))
const AdminProdReadinessPage  = lazy(() => import("@/pages/admin/ProductionReadiness"))
const AdminRulesRegistryPage  = lazy(() => import("@/pages/admin/RulesRegistry"))
const AdminLegalPage          = lazy(() => import("@/pages/admin/Legal"))
const PrivacyPage             = lazy(() => import("@/pages/legal/Privacy"))
const TermsPage               = lazy(() => import("@/pages/legal/Terms"))
const RefundPage              = lazy(() => import("@/pages/legal/Refund"))
const ShippingPage            = lazy(() => import("@/pages/legal/Shipping"))
const ContactPage             = lazy(() => import("@/pages/legal/Contact"))
const SolutionsGallery        = lazy(() => import("@/pages/solutions/SolutionsGallery"))
const SolutionPage            = lazy(() => import("@/pages/solutions/SolutionPage"))
const IntegrationPage         = lazy(() => import("@/pages/developers/Integration"))
const TeamPage                = lazy(() => import("@/pages/account/Team"))
const SandboxPlayground       = lazy(() => import("@/pages/SandboxPlayground"))
const SessionPage             = lazy(() => import("@/pages/Session"))
const IntakeFormPage          = lazy(() => import("@/pages/IntakeForm"))
const InviteAcceptPage        = lazy(() => import("@/pages/InviteAccept"))
const BookPage                = lazy(() => import("@/pages/Book"))
const DesktopAuthPage         = lazy(() => import("@/pages/DesktopAuthPage"))
const DownloadPage            = lazy(() => import("@/pages/Download"))
const CloudLayout             = lazy(() => import("@/pages/cloud/CloudLayout"))
const CloudHome               = lazy(() => import("@/pages/cloud/CloudHome"))
const CloudSummary            = lazy(() => import("@/pages/cloud/CloudSummary"))
const CloudOnboarding         = lazy(() => import("@/pages/cloud/CloudOnboarding"))
const CloudInbox              = lazy(() => import("@/pages/cloud/CloudInbox"))
const CloudShared             = lazy(() => import("@/pages/cloud/CloudShared"))
const CloudInboxItem          = lazy(() => import("@/pages/_deprecated/d2c/DoctorShareView"))
const CloudSettings           = lazy(() => import("@/pages/cloud/CloudSettings"))
const KnowledgeSearch         = lazy(() => import("@/pages/cloud/KnowledgeSearch"))

const PageFallback = () => (
  <div className="flex h-screen items-center justify-center text-gray-400 text-sm">Loading…</div>
)

/** Legacy /doctor/* paths → unified /app/cloud routes (preserve inbox item id). */
function LegacyDoctorInboxRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/app/cloud/inbox/${id}` : "/app/cloud/inbox"} replace />
}

/** summarisenow /app — route patients to /patient/dashboard, doctors to /app/cloud. */
function D2cAppIndexRedirect() {
  const { isLoaded } = useClerkAuth()
  const [target, setTarget] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoaded) return
    let cancelled = false
    api.authD2c.me()
      .then((u) => {
        if (cancelled) return
        if (u.enabledRoles.includes("doctor")) setTarget("/app/cloud")
        else if (u.enabledRoles.includes("patient")) setTarget("/patient/dashboard")
        else setTarget("/login")
      })
      .catch(() => {
        if (!cancelled) setTarget("/login")
      })
    return () => { cancelled = true }
  }, [isLoaded])

  if (!isLoaded || !target) return <PageFallback />
  return <Navigate to={target} replace />
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const tier = useDoctorTier()
  if (loading || tier.loading) return <PageFallback />
  // Accept EITHER clinic session (users + tenants) OR D2C doctor session
  // (app_users only). Per tiered onboarding (project_d2c_doctor_tiered_onboarding):
  // L1 doctors land at /app/cloud with greyed cards; L2 = full unlock.
  const hasClinicSession = Boolean(user)
  // Any non-anonymous D2C tier counts as authed — including "level2", which
  // happens when upgrade.status === "approved" but bridgeClinicSession hasn't
  // minted the snow_session cookie yet. Without level2 here, an approved
  // doctor gets stuck in a redirect loop between /app/cloud and /login.
  const hasD2cDoctor = tier.tier !== "anonymous"
  if (!hasClinicSession && !hasD2cDoctor) return <Navigate to="/login" replace />
  return <>{children}</>
}

// Any feature page that requires the tenant's trial/subscription to be valid.
// Tenant management and billing pages bypass this so a user can always recover.
function ActiveTenantRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth()
  if (loading) return <PageFallback />
  if (!user)   return <Navigate to="/login" replace />
  if (!isAdmin && user.subscriptionStatus === "expired") {
    return <Navigate to="/app/billing" replace />
  }
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth()
  if (loading)  return <PageFallback />
  if (!user)    return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/app/dashboard" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route
          path="/"
          element={
            isMarketingHost()
              ? <LandingPage />                    /* snowmed.health → marketing landing */
              : <Navigate to="/login" replace />   /* summarisenow + platform + everything else → /login */
          }
        />
        {/* Desktop download lives at snowmed.health/download — also reachable from any host. */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund-policy" element={<RefundPage />} />
        <Route path="/shipping-policy" element={<ShippingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* D2C dashboards (summarisenow.snowmed.health). app_users session, distinct from /app/* clinic surface. */}
        <Route path="/patient/dashboard"      element={<D2cPatientDashboard />} />
        <Route path="/patient/summaries/:id"  element={<D2cPatientSummary />} />
        {/* Redirects: superseded by /app/cloud unified doctor surface */}
        <Route path="/doctor/dashboard"       element={<Navigate to="/app/cloud" replace />} />
        <Route path="/doctor/inbox"           element={<Navigate to="/app/cloud/inbox" replace />} />
        <Route path="/doctor/inbox/:id"       element={<LegacyDoctorInboxRedirect />} />
        <Route path="/doctor/settings"        element={<Navigate to="/app/cloud/settings" replace />} />
        <Route path="/app/upgrade"            element={<D2cDoctorUpgrade />} />
        <Route path="/admin" element={<Navigate to="/app/admin/app-settings" replace />} />
        {/* Public session form — patient opens token URL, no auth. */}
        <Route path="/s/:token" element={<SessionPage />} />
        {/* Hosted intake form for /v1/intake-sessions tokens — single-submit,
            schema-driven. MUST be declared before the catch-all /:entity/:token
            route below; React Router matches in order. */}
        <Route path="/intake/:token" element={<IntakeFormPage />} />
        {/* D2C public share — patient → doctor link from share email. MUST be
            declared before the catch-all /:entity/:token so it wins precedence. */}
        <Route path="/share/:token" element={<D2cPublicShare />} />
        {/* Patient-friendly URL on *.summary.to hosts: previsit.summary.to/<doctor>/<token>.
            React Router needs a real route — Vercel's path rewrite only changes which
            file is served (still index.html), not the URL the router sees. */}
        <Route path="/:entity/:token" element={<SessionPage />} />
        {/* Public invite acceptance — may redirect to /login then return here. */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        {/* Public SNOW-hosted booking page — referenced from Integration→Websites snippets. */}
        <Route path="/book/:slug" element={<BookPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/desktop-auth" element={<DesktopAuthPage />} />
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        {/* OAuth callback — must be outside PrivateRoute, token arrives as ?token= in URL */}
        <Route path="/app/auth/callback" element={<AuthCallbackPage />} />
        {/* Onboarding — inside PrivateRoute but outside Layout (no sidebar during onboarding) */}
        <Route path="/app/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />

        {/* SNOW Web — doctor product, no developer sidebar. */}
        <Route
          path="/app/cloud/onboarding"
          element={<PrivateRoute><CloudOnboarding /></PrivateRoute>}
        />
        <Route
          path="/app/cloud"
          element={<PrivateRoute><ErrorBoundary><CloudLayout /></ErrorBoundary></PrivateRoute>}
        >
          <Route index             element={<CloudHome />} />
          <Route path="inbox"      element={<CloudInbox />} />
          <Route path="inbox/:id"  element={<CloudInboxItem />} />
          <Route path="shared"     element={<CloudShared />} />
          <Route path="settings"   element={<CloudSettings />} />
          <Route path="knowledge"  element={<KnowledgeSearch />} />
          <Route path=":sessionId" element={<CloudSummary />} />
        </Route>

        <Route
          path="/app"
          element={
            <PrivateRoute>
              <ErrorBoundary>
                <Layout />
              </ErrorBoundary>
            </PrivateRoute>
          }
        >
          <Route index element={
            isDoctorHost()
              ? <D2cAppIndexRedirect />
              : <Navigate to="/app/dashboard" replace />
          } />
          <Route
            path="dashboard"
            element={
              isDoctorHost()
                ? <Navigate to="/app/cloud" replace />
                : <ActiveTenantRoute><DashboardPage /></ActiveTenantRoute>
            }
          />
          <Route path="api-keys"  element={<ApiKeysPage />} />
          <Route path="usage"     element={<UsagePage />} />
          <Route path="docs"       element={<DocsPage />} />
          <Route path="worklist"   element={<ActiveTenantRoute><WorklistPage /></ActiveTenantRoute>} />
          <Route path="summaries"  element={<ActiveTenantRoute><SummariesPage /></ActiveTenantRoute>} />
          <Route path="patients"   element={<ActiveTenantRoute><PatientsPage /></ActiveTenantRoute>} />
          <Route path="webhooks"   element={<WebhooksPage />} />
          <Route path="templates"  element={<TemplatesPage />} />
          <Route path="templates/new"      element={<TemplateBuilder />} />
          <Route path="templates/:id/edit" element={<TemplateBuilder />} />
          <Route path="doctor/templates" element={<DoctorTemplatesPage />} />
          <Route path="settings"   element={<SettingsPage />} />
          <Route path="tenants"     element={<TenantsUserPage />} />
          <Route path="tenants/new" element={<TenantNewPage />} />
          <Route path="billing"     element={<BillingPage />} />
          <Route path="billing/paywall" element={<PaywallPage />} />
          <Route path="admin/billing" element={<AdminRoute><AdminBillingPage /></AdminRoute>} />
          <Route path="team"        element={<TeamPage />} />

          {/* Solutions — catalog gallery + per-solution page */}
          <Route path="solutions"      element={<SolutionsGallery />} />
          <Route path="solutions/:id"  element={<SolutionPage />} />

          {/* Developers — unified Integration page (Websites / Keys / Webhooks / Applications / Devices) */}
          <Route path="integration" element={<IntegrationPage />} />
          <Route path="sandbox"     element={<SandboxPlayground />} />

          {/* Admin routes — platform-admin capability only */}
          <Route path="admin/tenants"      element={<AdminRoute><TenantsPage /></AdminRoute>} />
          <Route path="admin/clinical-ref" element={<AdminRoute><ClinicalReferencePage /></AdminRoute>} />
          <Route path="admin/research"     element={<AdminRoute><ResearchPage /></AdminRoute>} />
          <Route path="admin/review"       element={<AdminRoute><ReviewQueuePage /></AdminRoute>} />
          <Route path="admin/console"      element={<AdminRoute><ProductConsolePage /></AdminRoute>} />
          <Route path="admin/security"     element={<AdminRoute><AdminSecurityPage /></AdminRoute>} />
          <Route path="admin/observability" element={<AdminRoute><AdminObservabilityPage /></AdminRoute>} />
          <Route path="admin/templates"    element={<AdminRoute><AdminTemplatesPage /></AdminRoute>} />
          <Route path="admin/app-settings" element={<AdminRoute><AdminAppSettingsPage /></AdminRoute>} />
          <Route path="admin/validators"   element={<AdminRoute><AdminValidatorsPage /></AdminRoute>} />
          <Route path="admin/roadmap"      element={<AdminRoute><AdminRoadmapPage /></AdminRoute>} />
          <Route path="admin/desktop-decisions" element={<AdminRoute><AdminDesktopDecisionsPage /></AdminRoute>} />
          <Route path="admin/explore"      element={<AdminRoute><AdminExplorePage /></AdminRoute>} />
          <Route path="admin/doctor-approvals" element={<AdminRoute><AdminDoctorApprovals /></AdminRoute>} />
          <Route path="admin/production"   element={<AdminRoute><AdminProdReadinessPage /></AdminRoute>} />
          <Route path="admin/rules"        element={<AdminRoute><AdminRulesRegistryPage /></AdminRoute>} />
          <Route path="admin/legal"        element={<AdminRoute><AdminLegalPage /></AdminRoute>} />
        </Route>
        <Route path="*" element={
          <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center">
            <p className="text-5xl font-bold text-gray-900">404</p>
            <p className="text-sm text-gray-500">Page not found.</p>
            <a href="/" className="text-sm text-indigo-600 underline hover:text-indigo-500">Go home</a>
          </div>
        } />
      </Routes>
    </Suspense>
  )
}
